import { Typography } from '@breetometer/components';
import { CopyIcon } from 'BreetAssets';
import { Amount, RowSection } from 'BreetComponents';
import {
	copyToClipboard,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	formatCurrencyAmount,
	formatDateTime,
	getSafeObjectField,
} from 'BreetHelpers';
import { useCurrencyOutput } from 'BreetHooks';

import type { IWithdrawalsProps } from '../DialogWithdrawalHistoryDetails.helpers';
import RowBalance from './RowBalance';
import RowStatusNote from './RowStatusNote';

const WithdrawalsAddress = ({ withdrawalHistoryData }: IWithdrawalsProps) => {
	const currencyOutput = useCurrencyOutput();

	const fiatConfig = getSafeObjectField(
		CURRENCYCONFIG,
		withdrawalHistoryData.currency.toUpperCase() as CURRENCYCODETYPE,
		CURRENCYCONFIG.USD
	);

	return (
		<div className='transactiondetails_itemscontainer bg-primary-500'>
			<RowSection.Tuple
				leftLabel='Crypto Type'
				leftComponent={
					<div className='transactiondetails_value'>
						<Typography className='color-neutral-50'>{withdrawalHistoryData.doc.meta.token}</Typography>
					</div>
				}
				rightLabel='Network'
				rightComponent={
					<div className='transactiondetails_value'>
						<Typography className='color-neutral-50'>{withdrawalHistoryData.doc.meta.network}</Typography>
					</div>
				}
			/>
			<RowSection.Tuple
				leftLabel='Receive Amount'
				leftComponent={
					<div className='transactiondetails_value'>
						<Typography className='color-neutral-50'>
							<Amount>
								{formatCurrencyAmount(withdrawalHistoryData.doc.amount - withdrawalHistoryData.doc.fee, fiatConfig.code)}
							</Amount>
						</Typography>
					</div>
				}
				rightLabel='Network Fee'
				rightComponent={
					<div className='transactiondetails_value'>
						<Typography className='color-neutral-50'>
							{fiatConfig.symbol}
							<Amount>{currencyOutput(withdrawalHistoryData.doc.fee)}</Amount>
						</Typography>
					</div>
				}
			/>
			<RowSection.Single
				label='Wallet Address'
				component={
					<div className='transactiondetails_value'>
						<Typography className='color-neutral-50'>{withdrawalHistoryData.doc.meta.walletAddress}</Typography>
						<CopyIcon
							variant='colored'
							focusable
							aria-label='Copy wallet address'
							role='button'
							tabIndex={0}
							onClick={copyToClipboard(withdrawalHistoryData.doc.meta.walletAddress, 'Wallet Address copied successfully')}
							className='cursor_pointer hide-during-screenshot'
						/>
					</div>
				}
			/>
			<RowSection.Single
				label='Label'
				component={
					<div className='transactiondetails_value'>
						<Typography className='color-neutral-50'>{withdrawalHistoryData.doc.meta.label}</Typography>
					</div>
				}
			/>

			<RowSection.Single
				label='Transaction ID'
				component={
					<div className='transactiondetails_value'>
						<Typography className='color-neutral-50'>{withdrawalHistoryData.doc._id}</Typography>
						<CopyIcon
							variant='colored'
							focusable
							aria-label='Copy reference ID'
							role='button'
							tabIndex={0}
							onClick={copyToClipboard(withdrawalHistoryData.doc._id, 'Reference ID copied successfully')}
							className='cursor_pointer hide-during-screenshot'
						/>
					</div>
				}
			/>

			{!!withdrawalHistoryData.doc.meta.txLink && (
				<RowSection.Single
					label='Transaction Link'
					component={
						<div className='transactiondetails_value'>
							<Typography
								as='a'
								href={withdrawalHistoryData.doc.meta.txLink}
								target='_blank'
								rel='noopener noreferrer'
							>
								{withdrawalHistoryData.doc.meta.txLink}
							</Typography>
							<CopyIcon
								variant='colored'
								focusable
								aria-label='Copy transaction link'
								role='button'
								tabIndex={0}
								onClick={copyToClipboard(withdrawalHistoryData.doc.meta.txLink, 'Transaction Link copied successfully')}
								className='cursor_pointer hide-during-screenshot'
							/>
						</div>
					}
				/>
			)}

			{withdrawalHistoryData.status === 'completed' && (
				<RowBalance
					symbol={fiatConfig.symbol}
					before={currencyOutput(withdrawalHistoryData.doc.balanceBefore)}
					after={currencyOutput(withdrawalHistoryData.doc.balanceAfter)}
				/>
			)}

			<RowStatusNote
				status={withdrawalHistoryData.status}
				note={withdrawalHistoryData.doc.reason}
				noteStatus={withdrawalHistoryData.status !== 'completed' ? 'error' : 'success'}
			/>

			<RowSection.Single
				label='Date'
				component={
					<div className='transactiondetails_value'>
						<Typography className='color-neutral-50'>{formatDateTime(withdrawalHistoryData.updatedAt)}</Typography>
					</div>
				}
			/>
		</div>
	);
};

export default WithdrawalsAddress;
